import { useCallback, useEffect, useMemo } from 'react';
import { t } from '@lingui/macro';

import Loading from '@decisiv/ui-components/lib/components/Loading';

import { useAddAssetContext } from '../context/AddAssetContext';
import { collectAssetsToAdd } from '../utils/collectAssetsToAdd';
import { useBulkRegisterAssetsMutation } from '../hooks/useBulkRegisterAssetsMutation';
import { usePollAssetRegistrationStatus } from '../hooks/usePollAssetRegistrationStatus';

/**
 * Intermediate ("between steps") content shown while assets are added, after the
 * user leaves Add Unit # and before Summary is revealed. It fires the bulk
 * registration mutation, polls its status (the poll hook writes the added/failed
 * results into saved state), then advances to Summary. On failure it advances to
 * Summary flagged so the step can render its error.
 */
export default function AddingAssets() {
  const { savedState, setSavedState, setIntermediate, forward } =
    useAddAssetContext();

  const { depotId } = savedState.stepOne;
  const { unitNumbers } = savedState.addUnit;

  const assetsToAdd = useMemo(
    () => collectAssetsToAdd(savedState.stepTwo),
    [savedState.stepTwo],
  );

  const [bulkRegisterAssets] = useBulkRegisterAssetsMutation();

  const {
    startPolling,
    data: pollData,
    pollingFinished,
    error: pollError,
  } = usePollAssetRegistrationStatus();

  const fail = useCallback(() => {
    setSavedState((prev) => ({
      ...prev,
      registration: { ...prev.registration, failed: true },
    }));
    setIntermediate(false);
    forward();
  }, [setSavedState, setIntermediate, forward]);

  // Fire the bulk mutation once on mount. All required data (depot, assets, unit
  // numbers) is stable in context by the time registration starts.
  useEffect(() => {
    if (!depotId || assetsToAdd.length === 0) {
      fail();
      return;
    }

    const register = async () => {
      try {
        const { data } = await bulkRegisterAssets({
          variables: {
            depotId,
            // `unitNumber` is part of the proposed PD-3375 schema; the generated
            // input type does not yet include it, so widen the type here.
            assets: assetsToAdd.map((asset) => ({
              vin: asset.vin,
              make: asset.make ?? '',
              model: asset.model ?? '',
              year: asset.year ?? '',
              serialNumber:
                (asset as ValidatedAsset & { serialNumber?: string })
                  .serialNumber ?? asset.vin.slice(-8),
              engine: '',
              unitNumber: unitNumbers[asset.vin] || undefined,
            })) as BulkRegisterAssetsMutationVariables['assets'],
          },
        });

        const errors = data?.bulkRegisterAssets?.errors;
        if (errors && errors.length > 0) {
          fail();
          return;
        }

        const registrationId =
          data?.bulkRegisterAssets?.assetGroupingAssetRegistration?.id;

        if (!registrationId) {
          fail();
          return;
        }

        setSavedState((prev) => ({
          ...prev,
          registration: {
            ...prev.registration,
            assetsRegistrationId: registrationId,
          },
        }));
        startPolling(registrationId);
      } catch {
        fail();
      }
    };

    register();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once polling resolves, the poll hook has written the results into saved
  // state; advance to Summary (or flag failure).
  useEffect(() => {
    if (!pollingFinished) return;

    const statuses =
      pollData?.managedAssetGroupingAssetRegistration
        ?.assetRegistrationStatuses ?? [];

    // The backend marks the whole registration "failed" when *any* asset fails,
    // so a partial failure still needs to reach Summary (which shows the
    // added-vs-failed breakdown). Only a systemic failure — a poll/network
    // error, or a run that produced no per-asset results at all — shows the
    // error page.
    if (pollError || statuses.length === 0) {
      fail();
      return;
    }

    setIntermediate(false);
    forward();
  }, [pollingFinished, pollError, pollData, fail, setIntermediate, forward]);

  return (
    <Loading
      size="small"
      title={t`Adding asset(s)…`}
      description={t`Your assets are being processed. Closing this window won't interrupt it. Results will be available when complete.`}
    />
  );
}
